import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { CampaignDetailPage } from './ui/pages/CampaignDetailPage';
import { CouponDetailPage } from './ui/pages/CouponDetailPage';
import {
  ApprovalQueueRoute,
  BudgetsRoute,
  CampaignCalendarRoute,
  CouponsListRoute,
  ProductSetsRoute,
  PromoSettingsRoute,
  PromotionsListRoute,
} from './ui/pages/PromoTabRoutes';
import { PromoWorkspacePage } from './ui/pages/PromoWorkspacePage';
import { PromotionBuilderPage } from './ui/pages/PromotionBuilderPage';
import { SimulatorPanel } from './ui/components/SimulatorPanel';
import { PromoAuditPanel } from './ui/components/PromoAuditPanel';

import { RequireSection } from '@/shared/routing';

/**
 * Promo feature subtree, mounted under the authenticated `AppLayout` shell.
 *
 * Tabs are sub-routes of the workspace shell (deep-linkable — approval-queue/budget
 * notifications land on `/promo/approvals` / `/promo/budgets`, plan-011 §6). The builder and the
 * campaign/coupon detail pages are separate full-page routes, siblings of the shell, not tabs.
 *
 * <p>The whole subtree — tabs, builder and detail pages alike — sits behind one
 * `catalog.promo.view` guard. Guarding the workspace shell alone would leave
 * `/promo/promotions/:id` serving a campaign to anyone who had its link.
 */
export const promotionsRoutes: RouteObject = {
  path: 'promo',
  element: <RequireSection section="promo" />,
  children: [
    {
      element: <PromoWorkspacePage />,
      children: [
        { index: true, element: <Navigate to="/promo/calendar" replace /> },
        { path: 'calendar', element: <CampaignCalendarRoute /> },
        { path: 'promotions', element: <PromotionsListRoute /> },
        { path: 'coupons', element: <CouponsListRoute /> },
        { path: 'sets', element: <ProductSetsRoute /> },
        { path: 'budgets', element: <BudgetsRoute /> },
        { path: 'approvals', element: <ApprovalQueueRoute /> },
        { path: 'simulator', element: <SimulatorPanel /> },
        { path: 'audit', element: <PromoAuditPanel /> },
        { path: 'settings', element: <PromoSettingsRoute /> },
      ],
    },
    { path: 'promotions/new', element: <PromotionBuilderPage /> },
    { path: 'promotions/:promotionId', element: <PromotionBuilderPage /> },
    { path: 'campaigns/:campaignId', element: <CampaignDetailPage /> },
    { path: 'coupons/:couponId', element: <CouponDetailPage /> },
    { path: '*', element: <Navigate to="/promo/calendar" replace /> },
  ],
};
